import { get, merge } from 'lodash';
import { AmortissementReference, ProduitsReference } from '../../../../shared/services/donne-reference.service';
import { FrequenceEnum } from '../../../../shared/constants/frequence.config';
import { CapaciteModel, CapacitePartial } from '../../modeles/capacite.model';
import { EtapeDettesDataModel } from '../../modeles/etape-dettes-data.model';
import { FrequenceHelper } from '../../../../shared/helpers/frequence-helper/frequence-helper';
import { DepenseResidenceModel } from '../../modeles/depense-residence.model';

export class CapaciteHelper {
  static isEqualCapacity(capacite: CapaciteModel) {
    return capacite.valeurProprieteRealiste === capacite.valeurProprieteMaximale;
  }

  static getTotalBudget(capacite: CapaciteModel) {
    if (capacite.indicateurLimiteMontantVersement === 'O') {
      return capacite.montantLimiteVersementMensuel;
    } else if (capacite.indicateurMontantTotalDepenses === 'O') {
      return capacite.montantTotalDepenses;
    }

    return this.getTotalDepenses(capacite);
  }

  static getTotalDepenses(capacite: CapaciteModel) {
    if (capacite.indicateurMontantTotalDepenses === 'O') {
      return capacite.montantTotalDepenses;
    }

    let total = 0;

    for (const depense of capacite.depensesMensuels) {
      const montant = Number(depense.montant) > 0 ? Number(depense.montant) : 0;
      total += montant;
    }

    return total;
  }

  static getTotalEngagements(capacite: CapaciteModel | EtapeDettesDataModel, defaultFrequence?: FrequenceEnum) {
    if (!capacite.bilanSommaire) {
      return 0;
    }

    return Math.ceil(
      FrequenceHelper.getTotalPerPeriod(
        [
          {
            montant: capacite.indicateurCarteCredit === 'N' ? capacite.bilanSommaire.carteCredit : 0,
            frequence: capacite.bilanSommaire.codeFrequenceCarteCredit || defaultFrequence
          },
          {
            montant: capacite.bilanSommaire.margeCredit,
            frequence: capacite.bilanSommaire.codeFrequenceMargeCredit || defaultFrequence
          },
          {
            montant: capacite.bilanSommaire.pretPersonnel,
            frequence: capacite.bilanSommaire.codeFrequencePretPersonnel || defaultFrequence
          },
          {
            montant: capacite.bilanSommaire.pretAuto,
            frequence: capacite.bilanSommaire.codeFrequencePretAuto || defaultFrequence
          },
          {
            montant: capacite.bilanSommaire.autrePret,
            frequence: capacite.bilanSommaire.codeFrequenceAutrePret || defaultFrequence
          }
        ],
        FrequenceEnum.MENSUEL
      )
    );
  }

  static getTotalFraisResidence(depenses: DepenseResidenceModel) {
    return Math.ceil(
      FrequenceHelper.getTotalPerPeriod(
        [
          {
            montant: depenses.paiementsHypothecaires || 0,
            frequence: depenses.codeFrequencePaiementsHypothecaires || FrequenceEnum.MENSUEL
          },
          {
            montant: depenses.taxesMunicipales,
            frequence: depenses.codeFrequenceTaxesMunicipales
          },
          {
            montant: depenses.taxesScolaires,
            frequence: depenses.codeFrequenceTaxesScolaires
          },
          {
            montant: depenses.coutsEnergie,
            frequence: depenses.codeFrequenceCoutsEnergie
          },
          {
            montant: depenses.fraisCopropriete,
            frequence: depenses.codeFrequenceFraisCopropriete
          }
        ],
        FrequenceEnum.MENSUEL
      )
    );
  }

  private static formatTauxInteret(tauxInteret: string, langue: string) {
    return langue === 'fr' ? tauxInteret.replace(/\./g, ',') : tauxInteret.replace(/,/g, '.');
  }

  static getModalites(produit: ProduitsReference, amortissement: AmortissementReference, taux: string, langue: string) {
    return {
      produit: get(produit, 'label', '').toLowerCase(),
      amortissement: get(amortissement, 'label', '').toLowerCase(),
      taux: this.formatTauxInteret(taux, langue)
    };
  }

  static isCapaciteHaute(capacite: CapaciteModel) {
    return (
      !this.isCapaciteBasse(capacite) &&
      (capacite.valeurProprieteMaximale >= 1000000 || capacite.valeurProprieteRealiste >= 1000000)
    );
  }

  static isCapaciteBasse(capacite: CapaciteModel) {
    return capacite.montantPretMaximal <= 0 || capacite.montantPretRealiste <= 0;
  }

  static mergeCapacite(capacite: CapaciteModel, morceauCapacite: CapacitePartial): CapaciteModel {
    const result = merge(capacite, morceauCapacite);
    if (morceauCapacite && morceauCapacite.depensesMensuels) {
      // Ne pas merger les dépenses pour permettre de supprimer des éléments du array
      result.depensesMensuels = morceauCapacite.depensesMensuels;
    }

    if (morceauCapacite.indicateurLimiteMontantVersement === 'O') {
      result.depensesMensuels = [];
      result.montantTotalDepenses = null;
      result.indicateurMontantTotalDepenses = 'N';
    } else if (morceauCapacite.indicateurLimiteMontantVersement === 'N') {
      result.montantLimiteVersementMensuel = null;
    }

    if (morceauCapacite.bilanSommaire && morceauCapacite.indicateurCarteCredit) {
      // On doit s'assurer que les dettes sont à 0
      const bilan = result.bilanSommaire;
      result.bilanSommaire = {
        ...bilan,
        carteCredit: bilan.carteCredit || 0,
        codeFrequenceCarteCredit: bilan.carteCredit ? bilan.codeFrequenceCarteCredit : null,
        pretAuto: bilan.pretAuto || 0,
        codeFrequencePretAuto: bilan.pretAuto ? bilan.codeFrequencePretAuto : null,
        pretPersonnel: bilan.pretPersonnel || 0,
        codeFrequencePretPersonnel: bilan.pretPersonnel ? bilan.codeFrequencePretPersonnel : null,
        margeCredit: bilan.margeCredit || 0,
        codeFrequenceMargeCredit: bilan.margeCredit ? bilan.codeFrequenceMargeCredit : null,
        autrePret: bilan.autrePret || 0,
        codeFrequenceAutrePret: bilan.autrePret ? bilan.codeFrequenceAutrePret : null
      };
    }
    return result;
  }
}
